// Helpers for the editable recorded date/time + the title-detected suggestion.
// `titleRecordedAt` is the structurer's verbatim string: a local wall-clock
// "YYYY-MM-DDTHH:MM" or a date-only "YYYY-MM-DD". `recordedAt` is the meeting's
// stored timestamptz ISO. All comparisons are by epoch ms, so they are correct
// regardless of how each side was parsed.

const TOLERANCE_MS = 60_000;

export function titleHasTime(titleRecordedAt: string): boolean {
  return titleRecordedAt.includes("T");
}

// timestamptz ISO -> "YYYY-MM-DDTHH:MM" in the browser's LOCAL time (the value
// shape a <input type="datetime-local"> expects). "" for null/invalid.
export function toDatetimeLocal(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// The datetime-local value to load into the editor when the user accepts the
// title's suggested time. Timed title -> used verbatim. Date-only title ->
// keep the current time-of-day (never midnight, unless there is no current
// time at all).
export function computeSuggestionFill(
  titleRecordedAt: string,
  recordedAt: string | null,
): string {
  if (titleHasTime(titleRecordedAt)) {
    return titleRecordedAt.slice(0, 16);
  }
  const datePart = titleRecordedAt.slice(0, 10);
  const localCurrent = toDatetimeLocal(recordedAt);
  const timePart = localCurrent ? localCurrent.slice(11, 16) : "00:00";
  return `${datePart}T${timePart}`;
}

// Show the suggestion when a detected title time exists and represents a
// different moment than the current recorded_at (beyond a 60s tolerance).
export function shouldSuggestTitleTime(
  titleRecordedAt: string | null,
  recordedAt: string | null,
): boolean {
  if (!titleRecordedAt) return false;
  const fill = computeSuggestionFill(titleRecordedAt, recordedAt);
  const titleMs = new Date(fill).getTime();
  if (Number.isNaN(titleMs)) return false;
  if (!recordedAt) return true;
  const currentMs = new Date(recordedAt).getTime();
  if (Number.isNaN(currentMs)) return true;
  return Math.abs(titleMs - currentMs) > TOLERANCE_MS;
}
